import React from 'react';
import { Link } from 'react-router-dom';
import ContextualMedia from '../../components/ContextualMedia';
import CreationSetupGuide from './CreationSetupGuide';
import {
  BulletList,
  Card,
  GuideMediaComposition,
  IllustratedSteps,
  Prereqs,
  RelatedLinks,
  ResultNote,
  Shot,
  Troubleshooting,
  useGuideTheme,
} from './guideBlocks';
import { CAPTURE_BASES } from '../../lib/selectedCaptures';

export const JoinEvent: React.FC = () => {
  const { heroPrimary, heroSecondary } = useGuideTheme();
  return (
  <>
    <Prereqs items={['A motorcycle in your garage', 'Location permission for nearby results']} />
    <IllustratedSteps
      steps={[
        { title: 'Browse the dashboard', description: 'Switch between map, list, and hybrid views. Filter by type (Cruise, Hangout, Tour, Workshop) and sort by date or distance.', shot: { base: CAPTURE_BASES.dashboardMap, alt: 'Event map view in RidePulse', caption: 'Map view of nearby events' } },
        { title: 'Open an event', description: 'Tap any event card or map pin to see the route, requirements, and who\'s hosting.', shot: { base: 'Event Details/iOS - Event Details', alt: 'Event details screen in RidePulse', caption: 'Event details with RSVP' } },
        { title: 'Choose your bike & RSVP', description: "Pick which motorcycle you're bringing. You can override privacy settings per event, share an emergency contact, and opt in to GroupPulse tracking." },
        { title: 'Check in when you arrive', description: 'RSVP confirms your spot; the host check-in confirms you made it. See the check-in guide below.' },
      ]}
    />
    <div className="max-w-2xl mb-8">
      <h3 className="text-base font-semibold mb-3" style={{ color: heroPrimary, textWrap: 'balance' }}>Join an invite-only event</h3>
      <p className="text-sm leading-relaxed mb-3" style={{ color: heroSecondary, textWrap: 'pretty' }}>
        Install or update RidePulse and sign in, then open the host’s full invitation link. You’ll see an event preview first — previewing never joins you and never unlocks the roster, chat, or tracking. To join, choose your motorcycle and sharing settings and explicitly confirm Join.
      </p>
      <p className="text-sm leading-relaxed mb-3" style={{ color: heroSecondary, textWrap: 'pretty' }}>
        On supported Android builds, Events → “Enter invite code” lets you paste the host’s code, preview the event, then explicitly confirm Join. That action is still rolling out and isn’t on every installed build; iOS currently needs the full invitation link while its global code action is pending. When in doubt, update RidePulse and ask the host for the full link.
      </p>
    </div>
    <ResultNote>You're in. Event chat opens and ride updates reach you.</ResultNote>
    <Troubleshooting items={[
      'Not seeing nearby events? The dashboard needs location access. Allow it in your device settings.',
      'For an invite-only event, ask the host for an invitation link. A valid link previews the meetup and requirements before you join — previewing never grants membership, roster, chat, or tracking access.',
    ]} />
    <RelatedLinks links={[
      { id: 'add-motorcycle', label: 'Add a motorcycle' },
      { id: 'create-event', label: 'Create an invite-only event' },
      { id: 'check-in', label: 'Check-in' },
    ]} />
  </>
  );
};

export const CreateEvent: React.FC = () => {
  const { heroPrimary, heroSecondary } = useGuideTheme();
  return (
  <>
    <Prereqs items={['A completed profile', 'Room under your plan\u2019s active-event limit']} />
    <IllustratedSteps
      steps={[
        { title: 'Event Basics', description: 'Choose event type, title, description, date & time', shot: { base: 'snapshots/20261004T181317Z/Events/iOS - Events - Create Event', alt: 'Create event form in RidePulse', caption: 'The create-event form' } },
        { title: 'Location & Route', description: 'Set meetup location. Pro: Add waypoints with map visualization', shot: { base: 'snapshots/20261004T181317Z/Events/iOS - Events - Location Selector', alt: 'Event location selector in RidePulse', caption: 'Picking the meeting point' } },
        { title: 'Requirements', description: 'Choose rider experience and an engine-size requirement. Pro adds motorcycle brand and color rules.' },
        { title: 'Visibility', description: 'In Core Details, choose Public or Invite Only under Visibility.' },
        { title: 'Publish', description: 'Review and create the event, then choose how to share it.' },
      ]}
    />
    <div className="max-w-2xl mb-8">
      <h3 className="text-base font-semibold mb-3" style={{ color: heroPrimary, textWrap: 'balance' }}>Invite-only events</h3>
      <p className="text-sm leading-relaxed mb-3" style={{ color: heroSecondary, textWrap: 'pretty' }}>
        Choose Invite Only to require an invitation or the host’s code to join. After creation, invite friends in RidePulse or share the invitation link with other riders. A shared invitation does not require friendship.
      </p>
      <p className="text-sm leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>
        Recipients preview the event before joining — a valid link or code shows its title, type, time, meetup and requirements, and previewing never grants membership, roster, chat, or tracking access. Recipient steps are in <Link to="/help#join-event" className="font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded" style={{ color: 'inherit' }}>Find &amp; join</Link>.
      </p>
    </div>
    <CreationSetupGuide />
    <ResultNote>Your event is created. Share it with the riders you want to join.</ResultNote>
    <Troubleshooting items={[
      'Can\'t create another event? Free accounts are limited to 3 active events. A completed or cancelled event no longer counts.',
      'Ask the host to share the full invitation link.',
      'Hit a daily cap instead? Every account has per-day creation and joining limits that grow with account age. See Free & Pro plans.',
    ]} />
    <RelatedLinks links={[
      { id: 'edit-cancel', label: 'Edit or cancel' },
      { id: 'manage-participants', label: 'Manage participants' },
      { id: 'free-pro', label: 'Free & Pro plans' },
    ]} />
  </>
  );
};

export const ManageParticipants: React.FC = () => {
  const { heroPrimary } = useGuideTheme();
  return (
    <>
      <Prereqs items={['An active event with RSVPs']} />
      <Card className="md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-bold mb-3" style={{ color: heroPrimary }}>Communication</h3>
            <BulletList items={['Send welcome message when riders join', 'Remind about gear and requirements', 'Share weather updates and route changes', 'Answer questions and build excitement']} />
          </div>
          <div>
            <h3 className="text-sm font-bold mb-3" style={{ color: heroPrimary }}>Moderation</h3>
            <BulletList items={['Remove inappropriate messages', 'Mute disruptive users temporarily', 'Kick participants from event if needed', 'Prevent kicked users from rejoining']} />
          </div>
        </div>
      </Card>
      <p className="mt-6 text-sm" style={{ color: heroPrimary }}>
        The participant roster shows who joined, their motorcycles, check-in status, and the contact details they chose to share.
      </p>
      <RelatedLinks links={[
        { id: 'check-in', label: 'Check-in' },
        { id: 'host-chat', label: 'Host chat' },
      ]} />
    </>
  );
};
export const CheckIn: React.FC = () => {
  const { dsColors, heroPrimary, heroSecondary } = useGuideTheme();
  const methods = [
    { method: 'GPS Proximity', badge: 'Rider-initiated', description: 'Riders open the event and check in themselves once they are near the meetup. Location is verified at check-in.', benefits: ['Location checked at arrival', 'Each rider confirms their own arrival', 'Less work for the host'] },
    { method: 'Manual', badge: 'Host-managed', description: 'Mark riders present yourself from the participant list when they can\u2019t check in.', benefits: ['Full control', 'Useful when a rider cannot self-check in', 'Useful as a fallback'] },
  ];
  return (
    <>
      <Prereqs items={['You\u2019re the host', 'Riders have RSVPed', 'Check-in opens 30 minutes before the meetup time']} />
      <GuideMediaComposition
        context={
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {methods.map((m) => (
              <Card key={m.method}>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <h3 className="text-lg font-bold" style={{ color: heroPrimary }}>{m.method}</h3>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${dsColors.treadGray}1f`, color: heroSecondary }}>
                    {m.badge}
                  </span>
                </div>
                <p className="text-sm mb-4" style={{ color: heroSecondary, textWrap: 'pretty' }}>{m.description}</p>
                <BulletList items={m.benefits} />
              </Card>
            ))}
          </div>
        }
        shot={{ base: 'snapshots/20261004T181317Z/Event Details/iOS - Event Details - GPS Check-In', alt: 'RidePulse event screen showing the rider-initiated Check In control with sample data', caption: 'Rider-initiated check-in.' }}
      />
      <ResultNote>Check-ins lock motorcycle selection and confirm attendance for safety.</ResultNote>
      <p className="text-sm max-w-2xl mt-4" style={{ color: heroSecondary, textWrap: 'pretty' }}>
        For a ride event, the host checks in before using Start Event.
      </p>
      <RelatedLinks links={[
        { id: 'manage-participants', label: 'Manage participants' },
        { id: 'edit-cancel', label: 'Edit or cancel' },
      ]} />
    </>
  );
};
export const HostChat: React.FC = () => {
  const { heroPrimary, heroSecondary, dsColors } = useGuideTheme();
  return (
    <>
      <Prereqs items={['You\u2019re hosting the event']} />
      <ContextualMedia
        media={
          <Shot shot={{ base: 'Event Details/iOS - Event Details - Event Chat', alt: 'Event chat in RidePulse during the pre-ride phase', caption: 'Event chat during the pre-ride phase' }} />
        }
      >
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { title: 'Broadcast Updates', description: 'Send messages to all participants at once' },
            { title: 'Pin Messages', description: 'Keep important info at the top of chat' },
            { title: 'Moderate Content', description: 'Remove inappropriate messages' },
            { title: 'Mute Users', description: 'Silence disruptive participants' },
          ].map((f) => (
            <Card key={f.title}>
              <h3 className="font-semibold text-base mb-1" style={{ color: heroPrimary }}>{f.title}</h3>
              <p className="text-sm" style={{ color: heroSecondary, textWrap: 'pretty' }}>{f.description}</p>
            </Card>
          ))}
        </ul>
      </ContextualMedia>
      <div className="mt-6 max-w-2xl">
        <h3 className="text-base font-bold mb-2" style={{ color: heroPrimary }}>How long a chat stays</h3>
        <p className="text-sm leading-relaxed mb-2" style={{ color: heroSecondary, textWrap: 'pretty' }}>
          Event chats stay open while the event is running. When the event ends, the chat moves to the Archived tab, where it stays available for 24 hours before leaving your chat list.
        </p>
        <p className="text-sm leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>
          How stored message data is handled is covered in the{' '}
          <Link
            to="/legal/privacy"
            className="font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
            style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
          >
            Privacy Policy
          </Link>
          .
        </p>
      </div>
      <RelatedLinks links={[
        { id: 'manage-participants', label: 'Manage participants' },
        { id: 'edit-cancel', label: 'Edit or cancel' },
      ]} />
    </>
  );
};
